import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";

const AgencyBillingPage = () => {
  const { token, user } = useAuth();
  const { t } = useTranslation();

  const isAgencyOwner =
    user?.role === "AGENCY" && (!user?.agencyRole || user?.agencyRole === "OWNER");

  const [invoices, setInvoices] = useState(null);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const [invoiceUploads, setInvoiceUploads] = useState([]);
  const [invoiceUploadsLoading, setInvoiceUploadsLoading] = useState(false);

  useEffect(() => {
    if (!token || !isAgencyOwner) return;
    (async () => {
      try {
        setInvoicesLoading(true);
        const resp = await api.agencyInvoices(token, 6);
        setInvoices(resp);
      } catch (_) {
        setInvoices(null);
      } finally {
        setInvoicesLoading(false);
      }
    })();
  }, [token, isAgencyOwner]);

  useEffect(() => {
    if (!token || !isAgencyOwner) return;
    (async () => {
      try {
        setInvoiceUploadsLoading(true);
        const resp = await api.agencyInvoiceUploads(token);
        setInvoiceUploads(resp?.items || []);
      } catch (_) {
        setInvoiceUploads([]);
      } finally {
        setInvoiceUploadsLoading(false);
      }
    })();
  }, [token, isAgencyOwner]);

  const formatMoney = (amountCents, currency) => {
    if (amountCents == null) return "—";
    const n = Number(amountCents) / 100;
    try {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: currency || "EUR",
      }).format(n);
    } catch (_) {
      return `${n.toFixed(2)} ${currency || "EUR"}`;
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "invoice.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!isAgencyOwner) return <Navigate to="/agency" replace />;

  return (
    <div className="min-h-screen bg-muted">
      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <h1 className="text-xl font-semibold text-foreground">{t("agency.pages.billingTitle")}</h1>

        <div className="bg-background p-4 rounded shadow space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">{t("agency.invoicesTitle")}</h3>
            {invoices && !invoices.pricingConfigured && (
              <div className="text-[11px] text-muted-foreground">{t("agency.invoicesPricingNotConfigured")}</div>
            )}
          </div>

          {invoicesLoading ? (
            <div className="text-xs text-muted-foreground">{t("common.loading")}</div>
          ) : invoices?.items?.length ? (
            <div className="overflow-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="p-2">{t("agency.invoices.month")}</th>
                    <th className="p-2">{t("agency.invoices.orgActive")}</th>
                    <th className="p-2">Stempelkarten</th>
                    <th className="p-2">Newsletter</th>
                    <th className="p-2">{t("agency.invoices.emailsSent")}</th>
                    <th className="p-2">{t("agency.invoices.scans")}</th>
                    <th className="p-2">{t("agency.invoices.redeems")}</th>
                    <th className="p-2">{t("agency.invoices.total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.items.map((it) => (
                    <tr key={it.label} className="border-t">
                      <td className="p-2 font-medium">{it.label}</td>
                      <td className="p-2">{it.orgActive ?? 0}</td>
                      <td className="p-2">{it.stampCardsActive ?? 0}</td>
                      <td className="p-2">{it.newsletterActive ?? 0}</td>
                      <td className="p-2">{it.emailsSent ?? 0}</td>
                      <td className="p-2">{it.scans ?? 0}</td>
                      <td className="p-2">{it.redeems ?? 0}</td>
                      <td className="p-2">{formatMoney(it.amountCents, it.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">{t("agency.invoicesEmpty")}</div>
          )}
        </div>

        <div className="bg-background p-4 rounded shadow space-y-2 text-xs">
          <div className="text-[11px] text-muted-foreground">{t("agency.invoiceUploadsTitle")}</div>
          {invoiceUploadsLoading ? (
            <div className="text-xs text-muted-foreground">{t("common.loading")}</div>
          ) : !invoiceUploads?.length ? (
            <div className="text-xs text-muted-foreground">{t("agency.invoiceUploadsEmpty")}</div>
          ) : (
            <div className="space-y-2">
              {invoiceUploads.map((u) => (
                <div key={u.id} className="border rounded p-2 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{u.label}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{u.fileName}</div>
                  </div>
                  <button
                    className="text-xs underline"
                    type="button"
                    onClick={async () => {
                      try {
                        const { blob, filename } = await api.agencyInvoiceDownload(token, u.id);
                        downloadBlob(blob, filename || u.fileName);
                      } catch (_) {
                        // keep simple
                      }
                    }}
                  >
                    {t("agency.invoiceUploadsDownload")}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AgencyBillingPage;
